import React, { FC, useState, useMemo } from 'react'
import { Dialog } from '@ui/overlays/dialog/dialog';

import { DialogBody } from '@ui/overlays/dialog/dialog-body';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@common/http/query-client';
import { Checkbox } from '@ui/forms/toggle/checkbox';

import { Trans } from '@ui/i18n/trans';
import { DialogFooter } from '@ui/overlays/dialog/dialog-footer';

import { Button } from '@ui/buttons/button';
import { Skeleton } from '@ui/skeleton/skeleton';

import { NormalizedModel } from '@player/types';
import { AbsoluteDateRange } from '@ui/forms/input-field/date/date-range-picker/form-date-range-picker';

import { useDialogContext } from '@ui/overlays/dialog/dialog-context';

export interface KeysListProps {
    selected: string[]
    onApply: (keys: string[]) => void
    onClear?: () => void
    artists?: NormalizedModel[]
    genres?: NormalizedModel[]
    labels?: NormalizedModel[]
    dateRange: AbsoluteDateRange | null
}

export const KeysList: FC<KeysListProps> = ({ selected, onApply, onClear, dateRange, labels, genres, artists }) => {
    const [list, setList] = useState<string[]>(selected || [])

    const params = useMemo(() => ({
        genres: genres?.length ? genres.map(genre => genre.id).join(',') : undefined,
        artists: artists?.length ? artists.map(artist => artist.id).join(',') : undefined,
        dateRange: dateRange ? `${dateRange.start}/${dateRange.end}` : undefined,
        tags: labels?.length ? labels.map(label => label.id).join(',') : undefined,
    }), [genres, artists, dateRange, labels])

    const { close } = useDialogContext()
    const toggleKey = (key: string) => {
        if (list.includes(key)) {
            setList(list.filter(k => k !== key))
        } else {
            setList([...list, key])
        }
    }

    const query = useQuery({
        queryKey: ['keysList', params],
        queryFn: async () => {

            return apiClient.get('tracks/unique/keys', {
                params
            }).then((res) => res.data)
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        retry: false,
    })

    return (
        <Dialog className="md:max-w-[450px]">
            <DialogBody className="md:max-h-450" padding="p-10">
                <div className="grid pb-56 grid-cols-3 md:grid-cols-4 gap-2">
                    {query?.isLoading ? Array.from({ length: 100 })?.map((_, index) => (
                        <div key={`keyLoading_${index}`} className="flex w-full gap-10 px-10 py-5 items-center">
                            <Skeleton
                                variant="rect"
                                size="w-20 h-20"
                                className="shrink-0"
                            />

                            <Skeleton
                                variant="text"
                            />
                        </div>
                    )) : query?.data?.length ? query?.data?.map((key: string) => (
                        <label key={`key_${key}`} className="flex w-full gap-10 px-10 py-5 items-center">
                            <Checkbox
                                checked={list?.includes(key)}
                                onChange={() => toggleKey(key)}
                            />
                            <span className="text-sm text-center">{key}</span>
                        </label>
                    )) : (
                        <div className="col-span-4 pt-56 justify-center flex items-center text-center">
                            No kes found for the selected filters. <br />
                            Please try changing the filters.
                        </div>
                    )}
                </div>
                <DialogFooter className="border-t" padding="p-10">
                    <Button
                        variant="flat"
                        color="paper"
                        size="xs"
                        className="ml-auto rounded-sm"
                        onClick={() => {
                            close()
                            onClear?.()

                            setList([])
                        }}
                    >
                        <Trans message="Reset" />
                    </Button>

                    <Button
                        variant="flat"
                        color="primary"
                        size="xs"
                        className="ml-auto rounded-sm"
                        onClick={() => {
                            close()
                            onApply?.(list)
                        }}
                    >
                        <Trans message="Apply" />
                    </Button>
                </DialogFooter>
            </DialogBody>
        </Dialog>
    )
}
